import { Theme } from '@chronosphereio/chrono-ui';
import { AbsoluteTimeRange } from '@chronosphereio/core';
import { useThrottle } from '@chronosphereio/shared';
import { produce } from 'immer';
import { useEffect, useMemo, useState } from 'react';
import { MAX_DECIMAL_PLACES, TimeScale, TimeSeriesDatasetSource, TooltipSort, UnitOptions } from '@/core';
import { CursorData, PANEL_TOOLTIP_PADDING, ZRRawMouseEvent } from '../PanelTooltip';
import {
  MAX_THROTTLE_MS,
  MIN_THROTTLE_MS,
  SEPARATE_EMPHASIZED_SERIES_LIMIT,
  ShowEndTimeArgs,
  THROTTLE_INCREASE_SENSITIVITY,
  TOOLTIP_MAX_WIDTH,
  TOOLTIP_MIN_WIDTH,
  TooltipSeriesArray,
  TooltipSeriesInfo,
} from './tooltip-model';

type GetTooltipStylesOpts = {
  isLargeWidthEnabled?: boolean;
  /**
   * Max height to use. Note that the max height calculated for the tooltip styles will be smaller
   * than this value if a `containerElement` is provided and its height is smaller than this value.
   */
  maxHeight: number;
  /**
   * Optional container element. When provided, the size of the tooltip will be constrained to fit within it.
   */
  containerElement?: Element | null;
};

/**
 * Helper for tooltip positioning styles
 */
export function getTooltipStyles(
  theme: Theme,
  { containerElement, maxHeight: initMaxHeight, isLargeWidthEnabled = false }: GetTooltipStylesOpts
) {
  const containerBoundingRect = containerElement?.getBoundingClientRect();

  // If tooltip is attached to a container, set max height to the height of the container so tooltip does not get cut
  // off.
  const containedMaxHeight = containerBoundingRect
    ? containerBoundingRect.height - PANEL_TOOLTIP_PADDING
    : initMaxHeight;
  const maxHeight = Math.min(containedMaxHeight, initMaxHeight);

  // Make sure the width does not exceed the available horizontal space.
  const containerWidth = containerBoundingRect ? containerBoundingRect.width : window.innerWidth;
  const maxWidth = Math.min(TOOLTIP_MAX_WIDTH, containerWidth - PANEL_TOOLTIP_PADDING * 2);

  return {
    minWidth: isLargeWidthEnabled ? TOOLTIP_MIN_WIDTH.large : TOOLTIP_MIN_WIDTH.medium,
    maxWidth,
    maxHeight,
    padding: 0,
    position: 'absolute',
    top: 0,
    left: 0,
    backgroundColor: theme.palette.background.popover,
    borderRadius: '4px',
    boxShadow: theme.shadows[5],
    visibility: 'visible',
    transition: 'transform 0.1s ease-out',
    zIndex: theme.zIndex.tooltip,
    overflow: 'hidden',
    '&:hover': {
      overflowY: 'auto',
    },
  };
}

/**
 * Check if nearby series is an event
 */
export function isEvent(nearbySeries: TooltipSeriesArray) {
  return nearbySeries.length === 1 && nearbySeries[0]?.event !== undefined;
}

/**
 * Throttle tooltip dynamically as number of series grows, adjust sensitivity and scaleFactor based on performance feedback
 */
export function calculateThrottleWaitTime(totalSeries: number) {
  // Determines how quickly the throttle time increases as the number of series grows using a logarithmic scale.
  const scaleFactor = Math.log10(Math.max(totalSeries, 1));
  let waitTime = MIN_THROTTLE_MS + THROTTLE_INCREASE_SENSITIVITY * scaleFactor;

  // Clamp the wait time to ensure it adheres to the set bounds
  waitTime = Math.min(MAX_THROTTLE_MS, Math.max(waitTime, MIN_THROTTLE_MS));

  // Round to the nearest millisecond
  return Math.round(waitTime);
}

/**
 * Cursor position to use in tooltip, adjust responsiveness based on series in chart.
 */
export const useMousePosition = (totalSeries = 0): CursorData['coords'] => {
  const [coords, setCoords] = useState<CursorData['coords']>(null);

  const waitTimeMs = useMemo(() => {
    return calculateThrottleWaitTime(totalSeries);
  }, [totalSeries]);

  const setFromEvent = (e: ZRRawMouseEvent) => {
    setCoords({
      page: {
        x: e.pageX,
        y: e.pageY,
      },
      client: {
        x: e.clientX,
        y: e.clientY,
      },
      // Always use zrender mousemove coords since they handle browser inconsistencies for us
      // ex: Firefox and Chrome have slightly different implementations of offsetX and offsetY (see reset flicker bug in PR #41721)
      // more info: https://github.com/ecomfe/zrender/blob/5.5.0/src/core/event.ts#L46-L120
      plotCanvas: {
        // zrX and zrY is undefined when not hovering over a chart (by design) and in Edge browser (unclear why)
        // Fallback to offsetX and offsetY to fix Edge as a workaround
        x: e.zrX ?? e.offsetX,
        y: e.zrY ?? e.offsetY,
      },
      target: e.target,
    });
  };

  // Wait time is dynamic based on number of series
  const throttledSetFromEvent = useThrottle(setFromEvent, waitTimeMs);

  useEffect(() => {
    const eventListener = (event: MouseEvent) => {
      throttledSetFromEvent(event as ZRRawMouseEvent);
    };

    window.addEventListener('mousemove', eventListener);

    return () => {
      window.removeEventListener('mousemove', eventListener);
    };
  }, [throttledSetFromEvent]);

  return coords;
};

/*
 * [DEPRECATED] Check if two numbers are within a specified percentage range
 */
export function isWithinPercentageRange({
  valueToCheck,
  baseValue,
  percentage,
}: {
  valueToCheck: number;
  baseValue: number;
  percentage: number;
}): boolean {
  const range = (percentage / 100) * baseValue;
  const lowerBound = baseValue - range;
  const upperBound = baseValue + range;
  return valueToCheck >= lowerBound && valueToCheck <= upperBound;
}

const MIN_CURSOR_RANGE = 0.001;
/*
 * Check if two numbers are within a specified percentage range.
 * Account for PercentDecimal cases, multi y axis.
 */
export function isCloseToCursor({
  valueToCheck,
  baseValue,
  percentage,
}: {
  valueToCheck: number;
  baseValue: number;
  percentage: number;
}): boolean {
  const range = (percentage / 100) * baseValue;
  const effectiveRange = Math.max(range, MIN_CURSOR_RANGE);
  const lowerBound = baseValue - effectiveRange;
  const upperBound = baseValue + effectiveRange;
  return valueToCheck >= lowerBound && valueToCheck <= upperBound;
}

/**
 * If the index is the last index, return the end time of the time range.
 * Otherwise, return the start time of the next time series value.
 */
function getTimeRangeEndTime(
  index: number,
  values: TimeSeriesDatasetSource[],
  timeRange: AbsoluteTimeRange
): number | undefined {
  if (index === values.length - 1) {
    return new Date(timeRange.end).valueOf();
  } else {
    // set end time to the start time of the next time series value
    const next = values[index + 1];
    if (next) {
      return next[0];
    }
  }
  return undefined;
}

/**
 * Find the end time for a tooltip given a point in time. Mainly used for time series histogram.
 */
export function findTooltipEndTime({
  index,
  values,
  timeRange,
  timeScale,
  showEndTime,
}: {
  index: number;
  values: TimeSeriesDatasetSource[];
  timeRange: AbsoluteTimeRange;
  timeScale?: TimeScale;
  showEndTime?: ShowEndTimeArgs;
}) {
  let endTimeMs;
  if (values) {
    const timeSeriesTuple = values[index];
    // When using the stepMs strategy, the end time should equal the start time of the current data point plus its step size.
    if (showEndTime?.strategy === 'stepMs' && timeSeriesTuple && timeScale?.stepMs) {
      const startTimeMs = timeSeriesTuple[0];
      if (startTimeMs) {
        endTimeMs = startTimeMs + timeScale.stepMs;
      }
    } else {
      endTimeMs = getTimeRangeEndTime(index, values, timeRange);
    }
  }
  return endTimeMs;
}

/**
 * Check whether unit should have abbreviate set or not, based on enableTooltipPreciseValues prop.
 */
export function checkUnitPrecision(inputUnit: UnitOptions, enableTooltipPreciseValues = true): UnitOptions {
  return produce(inputUnit, (draft) => {
    // TODO: Always format y-axis with `abbreviate: true`
    // Then, we can make this chart-level configuration.
    if (enableTooltipPreciseValues && draft.kind === 'Decimal') {
      draft.abbreviate = false;
      draft.decimal_places = MAX_DECIMAL_PLACES;
    }
  });
}

/**
 * Sort tooltip series based on sort mode and stacking.
 */
export function sortTooltipSeries({
  series,
  isStacked,
  tooltipSort,
}: {
  series: TooltipSeriesArray;
  isStacked?: boolean;
  tooltipSort?: TooltipSort;
}): TooltipSeriesArray {
  const sorted = series.slice().sort((a, b) => {
    if (a.seriesIndex === null || b.seriesIndex === null) return 0;

    if (tooltipSort === 'None') {
      if (isStacked) {
        // Since ECharts renders stacked series from bottom to top, reverse to ensure
        // tooltip order matches the query results order.
        // TODO: change stackOrder to make this more clear: https://echarts.apache.org/en/option.html#series-bar.stackOrder
        return b.seriesIndex - a.seriesIndex;
      } else {
        // Otherwise, when sort 'None' is used, we need to sort by seriesIndex to ensure
        // tooltip series match order from query results.
        return a.seriesIndex - b.seriesIndex;
      }
    }

    if (tooltipSort === 'Descending') {
      return b.y - a.y;
    }

    // When visual.stack set, reverse tooltip order since ECharts renders stacked series from bottom to top.
    // Sorting is handled based on enableStackSort prop in panel processing so do not need to resort by value here.
    if (isStacked) {
      return b.seriesIndex - a.seriesIndex;
    }

    // Sort in descending order by default when not stacked and no explicit sort is set.
    return b.y - a.y;
  });

  return sorted;
}

/**
 * Helper function to extract the emphasized series from the sorted series array.
 * Used to render the emphasized series in a separate static section.
 */
export function getEmphasizedSeriesInfo({
  sortedSeries,
}: {
  sortedSeries: TooltipSeriesArray;
}): TooltipSeriesInfo | undefined {
  if (sortedSeries.length <= SEPARATE_EMPHASIZED_SERIES_LIMIT) {
    return undefined;
  }

  const emphasizedIndex = sortedSeries.findIndex((s) => s.shouldEmphasizeText);
  if (emphasizedIndex === -1) {
    return undefined;
  }

  // Extract if sorted emphasized series index is at or above the separate limit
  if (emphasizedIndex >= SEPARATE_EMPHASIZED_SERIES_LIMIT) {
    return sortedSeries[emphasizedIndex];
  }

  return undefined;
}
